/* eslint-disable react/jsx-no-target-blank */
import React, { ReactElement, useEffect, useState } from "react";
import { CloseIcon, SquareButton } from "@themuse/design-system";
import Tooltip from "@themuse/design-system/lib/components/Tooltip/Tooltip";

import { useAppState } from "hooks/AppContext";
import {
    getJobTrackingProps
} from "utils/tracking";
import { getLocations } from "utils/strings";
import { CompanyInformationModule } from "./CompanyInformationModule";
import { getInteractionDate, getJobLastInteraction } from "../utils/interactionHistory";
import { formatDateAsAge } from "utils/dates";
import { JobApplyButton } from "./JobApplyButton";

import styles from "./JobIndividualHeader.module.scss";

/**
 * Renders a message indicating when a job was last viewed, but only if it has not been applied to.
 */
export const ViewedMsg = (props: { interactionDate: number; }): ReactElement => {
    const { interactionDate } = props;
    return (
        <p className={styles.jobInteractionHistoryViewed}>Viewed on {getInteractionDate(interactionDate)}</p>
    );
};

const ForwardIcon = () => {
    return (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 18 18">
            <path
                stroke="#0A66C2"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M8.068 1h-7v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-5m-11 2 11-11m0 0h-5m5 0v5"
            />
        </svg>
    )
}

/**
 * Closes the SideBySide modal by sending a message to the top window.
 */
const closeSideBySide = () => {
    window.top.postMessage("closeSideBySide", "*");
};

/**
 * Renders the header component for an individual job.
 *
 * This component displays the company name, job title, posted date, location, and company information.
 * It also tracks user interactions and applies appropriate styling based on the user's actions.
 *
 * @returns {ReactElement} The rendered header component for an individual job.
 */
export const JobHeader = (props: {
    setIsOpen?: (x: boolean) => void;
    isFrame?: boolean;
    innerRef?: React.LegacyRef<HTMLElement>;
}): ReactElement => {
    const { setIsOpen, innerRef, isFrame } = props;
    const { job, company } = useAppState();
    const [interactionDate, setInteractionDate] = useState<number>(null);
    const [showViewedMsg, setShowViewedMsg] = useState<boolean>(false);
    const [formattedDate, setFormattedDate] = useState<string>("");
    const {
        name: jobName, posted_at: postedDate, locations = [], id, state, ats_info, short_name: jobShortName
    } = job;
    const {
        short_name: companyShortName,
        package: pkg,
        name: companyName
    } = company;
    const { directApplyService = "" } = ats_info || {};
    const { dispLocation, tooltipProps } = getLocations(locations);
    const jobTrackingProps = getJobTrackingProps(job, company);

    // check previous interactions with this page only once.
    useEffect(() => {
        // always manipulate dates on the client.
        setFormattedDate(formatDateAsAge(postedDate));
        const lastInteraction = getJobLastInteraction(id);
        if (lastInteraction) {
            setInteractionDate(lastInteraction?.dt);
            setShowViewedMsg(!isFrame && !lastInteraction?.app);
        }
    }, [id, isFrame, postedDate]);
    // get live interactions as they change.
    useEffect(() => {
        const checkApplyStatus = () => {
            const interaction = getJobLastInteraction(id);
            if (interaction?.app) {
                setShowViewedMsg(false);
            }
        };
        window.addEventListener("storage", checkApplyStatus);
        return () => {
            window.removeEventListener("storage", checkApplyStatus);
        };
    }, [id]);

    const trackJobApplyProps = {
        ...jobTrackingProps,
        placement_position: "main",
        sp_click_position: "main"
    };
    const redirectUrl = `/jobs/${companyShortName}/${jobShortName}`;

        // used to test if the parent page is mobile as the Iframe has no idea.
        const [isMobile, setIsMobile] = useState(false);
        useEffect(() => {
            const handleResize = () => {
              setIsMobile(!window.parent.matchMedia("(min-width: 992px)").matches);
            };

            handleResize();

            window.addEventListener('resize', handleResize);

            return () => {
              window.removeEventListener('resize', handleResize);
            };
        }, []);

    return (
        <>
            <header ref={innerRef} className={styles.jobHeaderContainer}>
                {pkg !== "Programmatic"
                    ? (
                        <a
                            href={`/profiles/${company.short_name}`}
                            className={styles.jobHeaderCompanyName}
                            target="_blank"
                        >
                            {companyName}
                        </a>
                    )
                    : <p className={styles.jobHeaderCompanyNameProgrammatic}>{companyName}</p>
                }
                <h1 className={styles.jobHeaderTitle}>
                    {jobName}
                    {isFrame ? (
                        <a href={redirectUrl} target="_blank" className={styles.newWindow}>
                            <ForwardIcon />
                        </a>
                    ) : null}

                </h1>
                <div className={styles.jobHeaderSubHeading}>
                    { formattedDate }
                    <span className={styles.jobHeaderSeparator}>&bull;</span>
                    <span className={styles.jobHeaderLocation}>{dispLocation}</span>
                    {locations.length > 1 ? <Tooltip {...tooltipProps} /> : null}
                    {showViewedMsg && <ViewedMsg interactionDate={interactionDate} />}
                    {state === "live" ? (
                        <JobApplyButton
                            className={`${styles.applyButton} ${isFrame ? styles.framed : ""}`}
                            jobId={id}
                            trackingProps={trackJobApplyProps}
                            setIsOpen={setIsOpen}
                            isDirectApply={!!directApplyService}
                        />
                    ) : null}
                    {state !== "live" ? (
                        <div className={styles.archived}>
                        <p className={styles.archivedJobMessage}>This job is no longer available.</p>
                        {!isFrame ? (
                            <SquareButton
                                className={`${styles.archivedButton} ${styles.isFrame}`}
                                href="/search"
                                target="_blank"
                                color="mint"
                            >
                                Search all jobs
                            </SquareButton>
                        ) : null}
                        </div>
                    ) : null}
                </div>
                {isFrame && isMobile ? (
                    <button  className={styles.closeIframe}>
                        <CloseIcon onClick={closeSideBySide} />
                    </button>
                ) : null
                }
            </header>
            {pkg !== "Programmatic"
                ? (<CompanyInformationModule company={company} displayOnMobile={false} />)
                : null
            }

        </>
    );
};
JobHeader.displayName = "JobIndividualHeader";

export const JobIndividualHeader = React.memo(JobHeader);
